
type DatepickerOptions = {
    "format": string,
    "includeTime": boolean,
}


class Datepicker {
    elContainer: HTMLElement;
    elPreview:   HTMLInputElement;
    elValue:   HTMLInputElement;
    elMenu:   HTMLElement;

    value: Date = new Date();
    includeTime: boolean;
    format: string;

    currentYear: number;
    currentMonth: number;

    monthName: HTMLElement;
    weeksGrid: Array<HTMLElement> = [];
    daysGrid: Array<HTMLElement> = [];

    constructor(container: Element, opts: Partial<DatepickerOptions> = {}) {
        this.elContainer  = container as HTMLInputElement;

        this.elPreview    = container.querySelector('.datepicker-preview') as HTMLInputElement;
        this.elValue      = container.querySelector('.datepicker-value') as HTMLInputElement;
        this.elMenu       = container.querySelector('.datepicker-menu');

        this.includeTime = opts.includeTime || false;

        this.format = opts.format || (this.includeTime ? 'd.m.Y H:i:s' : 'd.m.Y');

        this.currentMonth = new Date().getMonth();
        this.currentYear  = new Date().getFullYear();

        if (this.elValue.value != "") {
            const date = this.parseDate(this.elValue.value);

            if (date) {
                // Initial setValue does not trigger change event
                this.setValue(date, true, false);
            }
        }

        this.init();
    }

    parseDate(input: string): Date|null {
        let date: Date = null;

        let inputDate = input.trim();
        let inputTime = "";

        if (this.includeTime) {
            // Split input into date + time

            let parts = inputDate.split(' ', 2);

            inputDate = parts[0];
            inputTime = parts[1] || "";
        }
        
        if (inputDate.length > 8) {
            const f1 = inputDate.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);

            if (f1) {
                // Set date
                date = new Date(parseInt(f1[3]), parseInt(f1[2])-1, parseInt(f1[1]));
            } else {
                const f2 = inputDate.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

                if (f2) {
                    date = new Date(parseInt(f2[1]), parseInt(f2[2])-1, parseInt(f2[3]));
                }
            }
        }

        if (date && inputTime.length > 0) {

            const f1 = inputTime.match(/^(\d{1,2})((:|h)(\d{1,2})(:(\d{1,2}))?)?$/);

            if (f1) {
                date.setHours(parseInt(f1[1]));

                if (f1[4]) {
                    date.setMinutes(parseInt(f1[4]));

                    if (f1[6]) {
                        date.setSeconds(parseInt(f1[6]));
                    }
                }
            }
        }

        return date;
    }
 
    init() {
        this.elPreview.addEventListener('focus', (e) => {
            this.openMenu();
        });

        let previewChanged = false;

        this.elPreview.addEventListener('input', (e) => {
            // Set value if valid date (preview or underlying)
            const value = this.elPreview.value;

            const date = this.parseDate(value);

            previewChanged = true;

            if (date) {
                this.setValue(date, false, true);

                if (date.getMonth() != this.currentMonth || date.getFullYear() != this.currentYear) {
                    this.currentMonth = date.getMonth();
                    this.currentYear  = date.getFullYear();

                    this.previewCurrentMonth();
                }
            } else if (value == "") {
                this.clearValue();
            }
        })

        this.elPreview.addEventListener('change', (e) => {
            if (previewChanged) {
                const value = this.elPreview.value;

                const date = this.parseDate(value);

                if (date) {
                    this.setValue(date, true, true);
                } else {
                    this.clearValue();
                }

                previewChanged = false;
            }
        });
    }

    toggleMenu() {
        if (this.elMenu.classList.contains('open')) {
            this.closeMenu()
        } else {
            this.openMenu()
        }
    }

    openMenu() {
        this.elMenu.classList.add('open');

        if (!this.elMenu.classList.contains("initialized")) {
            this.initMenu();
            this.elMenu.classList.add("initialized")
        }
    }

    initMenu() {
        const m = this.elMenu;

        const im = document.createElement('div');
        im.className = 'inner-menu';
        m.append(im);

        const md = document.createElement('div');
        md.className = 'inner-menu-date';
        im.append(md);

        // Month Selector
        const mSel = document.createElement('div')
        mSel.className = 'month-selector'

        const mName = document.createElement('div')
        mName.className = 'month-name'
        mName.innerText = 'Month Year'
        this.monthName = mName;

        const mButtons = document.createElement('div')
        mButtons.className = 'month-selector-buttons'

        const mPrev = document.createElement('button')
        mPrev.className = 'month-selector-prev'
        mPrev.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4"> <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>';

        const mNext = document.createElement('button')
        mNext.className = 'month-selector-next'
        mNext.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4"> <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" /></svg>';

        mButtons.append(mPrev)
        mButtons.append(mNext)

        mSel.append(mName)
        mSel.append(mButtons)

        md.append(mSel)

        // Week header
        const wHead = document.createElement('div')
        wHead.className = 'week-header week'

        for (let d = 0; d < 7; d++) {

            const date = new Date(2024, 0, d+1);

            const day = document.createElement('span')

            day.innerText = date.toLocaleString('default', { weekday: 'narrow' });
            
            if (d >= 5) {
                day.className = 'day-weekend';
            } else {
                day.className = 'day-week';
            }

            wHead.append(day);
        }

        md.append(wHead);

        // Days grid
        for (let d = 0; d < 42; d++) {

            const day = document.createElement("button");
            day.innerText = ""+d;
            day.className = "day"
            if (d % 7 >= 5) {
                day.classList.add('day-weekend');
            } else {
                day.classList.add('day-week');
            }

            this.daysGrid.push(day)
        }

        // Split days into weeks
        for (let w = 0; w < 6; w++) {
            const week = document.createElement("div");
            week.className = "week";

            const dows = this.daysGrid.slice(w*7, (w+1)*7);

            dows.forEach((d) => {
                week.append(d)
            })

            this.weeksGrid.push(week);

            md.append(week);
        }

        // Set click handlers

        mPrev.addEventListener("click", (e) => {
            e.preventDefault();
            this.gotoLastMonth();
        })

        mNext.addEventListener("click", (e) => {
            e.preventDefault();
            this.gotoNextMonth();
        })

        this.daysGrid.forEach((el, i) => {
            el.addEventListener("click", (e) => {
                e.preventDefault();
                this.selectDateByIndex(i)
            })
        })


        if (this.includeTime) {
            const mt = document.createElement('div');
            mt.className = 'inner-menu-time';
            im.append(mt);


        }

        this.previewCurrentMonth();
    }

    previewCurrentMonth() {
        // first day of month
        const fd   = new Date(this.currentYear, this.currentMonth, 1);
        // last day of last month
        const ldlm   = new Date(this.currentYear, this.currentMonth, 0);
        // last day of month
        const ld = (this.currentMonth == 11) ? new Date(this.currentYear+1, 0, 0) : new Date(this.currentYear, this.currentMonth+1, 0);

        // Set month+year
        this.monthName.innerText = fd.toLocaleString('default', { month: 'long' })+" "+fd.getFullYear();

        const fdom = (fd.getDay() + 6) % 7;
        const ldom = ld.getDate() + fdom - 1;

        for(let i = fdom-1; i >= 0; i--) {
            let day = ldlm.getDate() + (i - fdom + 1);

            this.daysGrid[i].innerText = ""+day;
            this.daysGrid[i].classList.add('day-around');
        }

        // first day of month is at index fdom
        let day = 1;

        for(let i = fdom; i <= ldom; i++) {
            this.daysGrid[i].innerText = ""+day;
            this.daysGrid[i].classList.remove('day-around');
            day++;
        }

        day = 1;
        for(let i = ldom+1; i < 42; i++) {
            this.daysGrid[i].innerText = ""+day;
            this.daysGrid[i].classList.add('day-around');
            day++;
        }
    }

    selectDateByIndex(index: number) {
        // first day of month
        const fd   = new Date(this.currentYear, this.currentMonth, 1);
        // last day of last month
        const ldlm   = new Date(this.currentYear, this.currentMonth, 0);
        // last day of month
        const ld = (this.currentMonth == 11) ? new Date(this.currentYear+1, 0, 0) : new Date(this.currentYear, this.currentMonth+1, 0);

        const fdom = fd.getDay() == 0 ? 6 : ((fd.getDay() - 1) % 7);
        const ldom = ld.getDate() + fdom;

        let date: Date;

        if (index < fdom) {
            // Past month
            if (this.currentMonth == 0) {
                date = new Date(this.currentYear-1, 11, index - fdom + ldlm.getDate() + 1);
            } else {
                date = new Date(this.currentYear, this.currentMonth-1, index - fdom + ldlm.getDate() + 1);
            }
        } else if (index > ldom) {
            // Next month
            if (this.currentMonth == 11) {
                date = new Date(this.currentYear+1, 0, index - ldom + 1);
            } else {
                date = new Date(this.currentYear, this.currentMonth+1, index - ldom + 1);
            }
        } else {
            // Current month
            date = new Date(this.currentYear, this.currentMonth, index - fdom + 1);
        }

        if (this.includeTime) {
            date.setHours(this.value.getHours());
            date.setMinutes(this.value.getMinutes());
            date.setSeconds(this.value.getSeconds());
        }

        this.setValue(date, true, true);
    }

    setValue(date: Date, updatePreview: boolean, triggerChange: boolean) {

        let yyyy = ""+date.getFullYear();
        let mm = ""+(date.getMonth()+1);
        if (mm.length < 2) {
            mm = "0"+mm;
        }
        let dd = ""+date.getDate();
        if (dd.length < 2) {
            dd = "0"+dd;
        }

        let hh = ""+date.getHours();
        if (hh.length < 2) {
            hh = "0"+hh;
        }
        let ii = ""+date.getMinutes();
        if (ii.length < 2) {
            ii = "0"+ii;
        }
        let ss = ""+date.getSeconds();
        if (ss.length < 2) {
            ss = "0"+ss;
        }

        const oldValue = this.elValue.value;


        if (this.includeTime) {
            this.elValue.value = yyyy+"-"+mm+"-"+dd+" "+hh+":"+ii+":"+ss;
        } else {
            this.elValue.value = yyyy+"-"+mm+"-"+dd;
        }

        const newValue = this.elValue.value;

        // Preview
        if (updatePreview) {
            
            let preview = this.format;

            const replaces = new Map<string, string>([
                ["Y", yyyy],
                ["m", mm],
                ["d", dd],
                ["H", hh],
                ["i", ii],
                ["s", ss],
            ])

            for(let [k, v] of replaces) {
                preview = preview.replace(k, v);
            }

            this.elPreview.value = preview;

            this.currentMonth = date.getMonth();
            this.currentYear  = date.getFullYear();
        }

        this.value = date;

        // Do not close if preview has focus
        if (document.activeElement != this.elPreview) {
            this.closeMenu();
        }

        if (triggerChange && oldValue != newValue) {
            this.elValue.dispatchEvent(new Event("change"))
        }
    }

    clearValue() {
        const oldValue = this.elValue.value;
        this.elValue.value = "";
        const newValue = this.elValue.value;
        this.elPreview.value = "";

        if (oldValue != newValue) {
            this.elValue.dispatchEvent(new Event("change"))
        }
    }

    gotoNextMonth() {
        if (this.currentMonth == 11) {
            this.currentMonth = 0;
            this.currentYear++;
        } else {
            this.currentMonth++;
        }

        this.previewCurrentMonth()
    }

    gotoLastMonth() {
        if (this.currentMonth == 0) {
            this.currentMonth = 11;
            this.currentYear--;
        } else {
            this.currentMonth--;
        }

        this.previewCurrentMonth()
    }

    closeMenu() {
        this.elMenu.classList.remove('open');
    }

}

// Close datepickers
window.addEventListener('click', (e) => {
  const el = (e.target as Element);

  let thisDatepicker: Element|null = null;

  if (el.matches(".datepicker-container *")) {
    thisDatepicker = el.closest(".datepicker-container");
  }

  // Close all other multiselects
  const menus = document.querySelectorAll(".datepicker-container > .datepicker-menu.open");

  for (let i = 0; i < menus.length; i++) {
    const menu = menus[i];

    if (menu.closest(".datepicker-container") !== thisDatepicker) {
      menu.classList.remove("open");
    }
  }
})

export { Datepicker };